import { useAuth } from "../modules/auth/context/AuthContext";
import { login } from "../modules/auth/authService";
import { getInitials } from "../utils/initials";
import {
  AuthBlockStyled,
  ProfileLink,
  AvatarCircle,
  UserName,
  AdminIconLink,
  LogoutLink,
  SwitchUserBtn,
  InlineIconsGroup,
  MenuUserCard,
  MenuUserInfo,
  MenuUserName,
  MenuUserEmail,
  MenuRoleBadge,
  MenuDivider,
  MenuItemLink,
  MenuItemButton,
} from "./authBlock.module";

const ROLE_HIERARCHY = {
  USER: 0,
  ADMIN: 100,
};

const ProfileIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const AdminIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const SwitchUserIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 3h5v5" />
    <path d="M8 3H3v5" />
    <path d="M3 16v5h5" />
    <path d="M21 16v5h-5" />
    <path d="M21 12h-4.05A3 3 0 0 0 14 14.95V21" />
    <path d="M3 12h4.05A3 3 0 0 1 10 9.05V3" />
  </svg>
);

const LogoutIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const LoginIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
    <polyline points="10 17 15 12 10 7" />
    <line x1="15" y1="12" x2="3" y2="12" />
  </svg>
);

const AuthBlock = ({ variant = "inline" }) => {
  const { user, isLoading } = useAuth();
  const userLevel = ROLE_HIERARCHY[user?.globalRole] ?? 0;
  const isAdmin = userLevel >= ROLE_HIERARCHY.ADMIN;
  const showSwitchInHeader = user?.showSwitchUserInHeader === true;

  const handleLogin = (e) => {
    e.preventDefault();
    login();
  };

  if (isLoading) {
    return null;
  }

  if (variant === "menu") {
    if (!user) {
      return (
        <MenuItemLink to="/auth/login" onClick={handleLogin}>
          <LoginIcon />
          Увійти
        </MenuItemLink>
      );
    }

    return (
      <>
        <MenuUserCard>
          <AvatarCircle $size={44}>
            {user.avatar ? (
              <img src={user.avatar} alt="avatar" />
            ) : (
              getInitials(user.firstName, user.lastName)
            )}
          </AvatarCircle>
          <MenuUserInfo>
            <MenuUserName>
              {user.firstName || ""} {user.lastName || ""}
            </MenuUserName>
            {user.email && <MenuUserEmail>{user.email}</MenuUserEmail>}
            {isAdmin && <MenuRoleBadge>Адміністратор</MenuRoleBadge>}
          </MenuUserInfo>
        </MenuUserCard>
        <MenuDivider />
        <MenuItemLink to="/auth/profile">
          <ProfileIcon />
          Профіль
        </MenuItemLink>
        {isAdmin && (
          <MenuItemLink to="/admin">
            <AdminIcon />
            Адміністрування
          </MenuItemLink>
        )}
        {showSwitchInHeader && (
          <MenuItemButton
            type="button"
            onClick={(e) => {
              e.preventDefault();
              login();
            }}
          >
            <SwitchUserIcon />
            Змінити користувача
          </MenuItemButton>
        )}
        <MenuDivider />
        <MenuItemLink to="/auth/logout" $danger>
          <LogoutIcon />
          Вийти
        </MenuItemLink>
      </>
    );
  }

  return (
    <AuthBlockStyled>
      {user ? (
        <>
          <ProfileLink to="/auth/profile">
            <UserName>
              {user.firstName || ""} {user.lastName || ""}
            </UserName>
            <AvatarCircle>
              {user.avatar ? (
                <img src={user.avatar} alt="avatar" />
              ) : (
                getInitials(user.firstName, user.lastName)
              )}
            </AvatarCircle>
          </ProfileLink>
          <InlineIconsGroup>
            {isAdmin && (
              <AdminIconLink to="/admin" aria-label="admin">
                <AdminIcon />
              </AdminIconLink>
            )}
            {showSwitchInHeader && (
              <SwitchUserBtn
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  login();
                }}
                aria-label="switch user"
                title="Змінити користувача"
              >
                <SwitchUserIcon />
              </SwitchUserBtn>
            )}
            <LogoutLink to="/auth/logout" aria-label="logout">
              <LogoutIcon />
            </LogoutLink>
          </InlineIconsGroup>
        </>
      ) : (
        <LogoutLink to="/auth/login" onClick={handleLogin} aria-label="login">
          <LoginIcon />
        </LogoutLink>
      )}
    </AuthBlockStyled>
  );
};

export default AuthBlock;
